import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../utils/api';

const ROLE_CONFIG = {
  SUPERADMIN: { label: 'Super Admin', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  MANAGER: { label: 'Manager', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  ACCOUNTANT: { label: 'Comptable', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  CONSULTANT: { label: 'Conseiller', color: 'bg-teal-500/20 text-teal-400 border-teal-500/30' },
  EMPLOYEE: { label: 'Employé', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  CLIENT: { label: 'Client', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
};

const getRoleInfo = (role) => ROLE_CONFIG[role] || { label: role, color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };

export default function UserManagement({ onDataChanged }) {
  // ── Data ──
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Search / Filter ──
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // ── Selection ──
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Dialogs ──
  const [detailUser, setDetailUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, confirmText: '' });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false, confirmText: '' });
  const [processing, setProcessing] = useState(null); // userId for toggle, 'delete' / 'bulk'

  // ────────────────────────────── Fetch ──────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      setUsers(res.data || []);
    } catch {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ────────────────────────────── Filtering ──────────────────────────────
  const filtered = useMemo(() => {
    let list = users;

    // Role filter
    if (roleFilter !== 'ALL') {
      list = list.filter(u => u.role === roleFilter);
    }

    // Status filter
    if (statusFilter === 'ACTIVE') list = list.filter(u => u.is_active);
    else if (statusFilter === 'INACTIVE') list = list.filter(u => !u.is_active);

    // Text search (case-insensitive across multiple fields)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(u =>
        (u.full_name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q) ||
        (u.country || '').toLowerCase().includes(q) ||
        (u.role || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [users, searchQuery, roleFilter, statusFilter]);

  // ────────────────────────────── Selection helpers ──────────────────────────────
  const selectableFiltered = useMemo(() => filtered.filter(u => u.role !== 'SUPERADMIN'), [filtered]);
  const allSelected = selectableFiltered.length > 0 && selectableFiltered.every(u => selectedIds.has(u.id));

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableFiltered.map(u => u.id)));
    }
  };

  // ────────────────────────────── Actions ──────────────────────────────
  const handleToggleStatus = async (u) => {
    if (u.role === 'SUPERADMIN') return;
    if (processing) return;
    setProcessing(u.id);
    try {
      const next = !u.is_active;
      await api.patch(`/admin/users/${u.id}`, { is_active: next });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, is_active: next } : x));
      toast.success(`Utilisateur ${next ? 'activé' : 'désactivé'}`);
      onDataChanged?.();
    } catch {
      toast.error('Échec de la mise à jour');
    } finally {
      setProcessing(null);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const res = await api.post('/admin/impersonate', { target_user_id: userId });
      const { impersonation_token, target_user } = res.data;

      // Save the original SuperAdmin token to restore later
      const originalToken = localStorage.getItem('token');
      if (originalToken) {
        localStorage.setItem('original_admin_token', originalToken);
      }

      // Replace the active token so AuthContext uses the impersonation one
      localStorage.setItem('token', impersonation_token);
      localStorage.setItem('token_timestamp', Date.now().toString());
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('user_role', target_user.role);
      localStorage.setItem('user_id', target_user.id);

      toast.success(`Impersonnification de ${target_user.name} réussie`);

      // Redirect to the correct role-based dashboard
      const rolePaths = {
        SUPERADMIN: '/superadmin/dashboard',
        MANAGER: '/manager/dashboard',
        ACCOUNTANT: '/accountant/dashboard',
        CONSULTANT: '/consultant/dashboard',
        EMPLOYEE: '/employee/dashboard',
        CLIENT: '/client/dashboard',
      };
      window.location.href = rolePaths[target_user.role] || '/';
    } catch {
      toast.error("Erreur lors de l'impersonnification");
    }
  };

  const confirmDelete = async () => {
    const target = deleteDialog.user;
    if (!target) return;
    const expected = target.full_name?.split(' ')[0]?.toLowerCase();
    if (deleteDialog.confirmText.toLowerCase() !== expected) {
      toast.error(`Saisissez « ${target.full_name?.split(' ')[0]} » pour confirmer`);
      return;
    }
    setProcessing('delete');
    try {
      await api.delete(`/admin/users/${target.id}`);
      setUsers(prev => prev.filter(x => x.id !== target.id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(target.id); return n; });
      toast.success(`« ${target.full_name} » supprimé`);
      setDeleteDialog({ open: false, user: null, confirmText: '' });
      onDataChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setProcessing(null);
    }
  };

  const confirmBulkDelete = async () => {
    if (bulkDeleteDialog.confirmText !== 'SUPPRIMER') {
      toast.error('Saisissez « SUPPRIMER » pour confirmer');
      return;
    }
    setProcessing('bulk');
    try {
      const res = await api.post('/admin/users/bulk-delete', { user_ids: Array.from(selectedIds) });
      toast.success(res.data.message);
      if (res.data.skipped_count > 0) toast.info(`${res.data.skipped_count} SuperAdmin(s) ignoré(s)`);
      setSelectedIds(new Set());
      setBulkDeleteDialog({ open: false, confirmText: '' });
      await fetchUsers();
      onDataChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression en masse');
    } finally {
      setProcessing(null);
    }
  };

  // ────────────────────────────── Role counts for filter chips ──────────────────────────────
  const roleCounts = useMemo(() => {
    const counts = { ALL: users.length };
    users.forEach(u => { counts[u.role] = (counts[u.role] || 0) + 1; });
    return counts;
  }, [users]);

  // ────────────────────────────── Render ──────────────────────────────
  if (loading) {
    return (
      <div className="bg-[#1E293B] rounded-lg p-12 border border-slate-700 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-400">Chargement des utilisateurs…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ─── Header bar ─── */}
      <div className="bg-[#1E293B] rounded-lg p-5 border border-slate-700">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h2 className="text-xl font-bold text-white">👥 Tous les utilisateurs</h2>
          <div className="flex items-center gap-3">
            {selectedIds.size > 0 && (
              <Button
                onClick={() => setBulkDeleteDialog({ open: true, confirmText: '' })}
                className="bg-red-600 hover:bg-red-700 text-sm"
              >
                🗑️ Supprimer la sélection ({selectedIds.size})
              </Button>
            )}
            <Button onClick={fetchUsers} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 text-sm">
              🔄 Actualiser
            </Button>
            <span className="text-slate-400 text-sm">{filtered.length} / {users.length} utilisateur(s)</span>
          </div>
        </div>

        {/* ─── Search ─── */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[250px]">
            <Input
              placeholder="Rechercher par nom, email, téléphone, pays…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-600 text-white text-sm rounded-md px-3 py-2"
          >
            <option value="ALL">Tous les statuts</option>
            <option value="ACTIVE">Actifs</option>
            <option value="INACTIVE">Inactifs</option>
          </select>
        </div>

        {/* ─── Role chips ─── */}
        <div className="flex flex-wrap gap-2 mt-3">
          {[
            { key: 'ALL', label: 'Tous' },
            ...Object.entries(ROLE_CONFIG).map(([key, cfg]) => ({ key, label: cfg.label }))
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setRoleFilter(key)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                roleFilter === key
                  ? 'bg-orange-600 text-white border-orange-500'
                  : 'bg-slate-800 text-slate-300 border-slate-600 hover:border-slate-500'
              }`}
            >
              {label} ({roleCounts[key] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="bg-[#1E293B] rounded-lg border border-slate-700 overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-800/50">
              <th className="p-4 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-orange-500 rounded"
                />
              </th>
              <th className="p-4 text-slate-300 font-medium text-sm">Utilisateur</th>
              <th className="p-4 text-slate-300 font-medium text-sm">Rôle</th>
              <th className="p-4 text-slate-300 font-medium text-sm">Contact</th>
              <th className="p-4 text-slate-300 font-medium text-sm">Statut</th>
              <th className="p-4 text-slate-300 font-medium text-sm">Inscrit le</th>
              <th className="p-4 text-slate-300 font-medium text-sm text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-10 text-center text-slate-500">
                  Aucun utilisateur ne correspond aux critères.
                </td>
              </tr>
            ) : (
              filtered.map((u) => {
                const roleInfo = getRoleInfo(u.role);
                const isSelected = selectedIds.has(u.id);
                const isSuperAdmin = u.role === 'SUPERADMIN';

                return (
                  <tr
                    key={u.id}
                    className={`border-b border-slate-700/50 hover:bg-slate-800/40 transition-colors ${isSelected ? 'bg-orange-500/5' : ''}`}
                  >
                    {/* Checkbox */}
                    <td className="p-4">
                      {!isSuperAdmin ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleOne(u.id)}
                          className="w-4 h-4 accent-orange-500 rounded"
                        />
                      ) : <div className="w-4 h-4" />}
                    </td>

                    {/* Name */}
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shrink-0">
                          <span className="text-white font-semibold text-sm">
                            {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <span className="text-white font-medium">{u.full_name || '—'}</span>
                      </div>
                    </td>

                    {/* Role */}
                    <td className="p-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${roleInfo.color}`}>
                        {roleInfo.label}
                      </span>
                    </td>

                    {/* Contact */}
                    <td className="p-4">
                      <p className="text-slate-300 text-sm truncate max-w-[200px]">{u.email}</p>
                      {u.phone && <p className="text-slate-500 text-xs mt-0.5">{u.phone}</p>}
                      {u.country && <p className="text-slate-500 text-xs mt-0.5">{u.country}</p>}
                    </td>

                    {/* Status */}
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        u.is_active
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : 'bg-red-500/15 text-red-400'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        {u.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="p-4 text-slate-400 text-sm whitespace-nowrap">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                    </td>

                    {/* Actions */}
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setDetailUser(u)}
                          className="px-2.5 py-1.5 text-xs rounded bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                          title="Voir les détails"
                        >
                          👁️ Voir
                        </button>
                        {!isSuperAdmin && (
                          <>
                            <button
                              onClick={() => handleImpersonate(u.id)}
                              className="px-2.5 py-1.5 text-xs rounded bg-orange-600 hover:bg-orange-700 text-white transition-colors"
                              title="Impersonner"
                            >
                              🎭
                            </button>
                            <button
                              onClick={() => handleToggleStatus(u)}
                              disabled={processing === u.id}
                              className={`px-2.5 py-1.5 text-xs rounded transition-colors ${
                                u.is_active
                                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                              } ${processing === u.id ? 'opacity-50 cursor-wait' : ''}`}
                              title={u.is_active ? 'Désactiver' : 'Activer'}
                            >
                              {processing === u.id ? '⏳' : u.is_active ? '⛔' : '✅'}
                            </button>
                            <button
                              onClick={() => setDeleteDialog({ open: true, user: u, confirmText: '' })}
                              className="px-2.5 py-1.5 text-xs rounded bg-red-600 hover:bg-red-700 text-white transition-colors"
                              title="Supprimer"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ═══════════════════════ Detail Dialog ═══════════════════════ */}
      <Dialog open={!!detailUser} onOpenChange={(open) => { if (!open) setDetailUser(null); }}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Détails de l'utilisateur</DialogTitle>
          </DialogHeader>
          {detailUser && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-4 pb-4 border-b border-slate-700">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{detailUser.full_name?.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{detailUser.full_name}</h3>
                  <span className={`inline-block px-3 py-0.5 rounded-full text-xs font-medium border ${getRoleInfo(detailUser.role).color}`}>
                    {getRoleInfo(detailUser.role).label}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <InfoCard label="Email" value={detailUser.email} />
                <InfoCard label="Téléphone" value={detailUser.phone || '—'} />
                <InfoCard label="Pays" value={detailUser.country || '—'} />
                <InfoCard
                  label="Statut"
                  value={detailUser.is_active ? '✅ Actif' : '❌ Inactif'}
                  valueClass={detailUser.is_active ? 'text-emerald-400' : 'text-red-400'}
                />
                <InfoCard
                  label="Inscrit le"
                  value={detailUser.created_at ? new Date(detailUser.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                />
                <InfoCard
                  label="Dernière connexion"
                  value={detailUser.last_login ? new Date(detailUser.last_login).toLocaleDateString('fr-FR') : 'Jamais'}
                />
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-700">
                <Button onClick={() => setDetailUser(null)} className="flex-1 bg-slate-700 hover:bg-slate-600">
                  Fermer
                </Button>
                {detailUser.role !== 'SUPERADMIN' && (
                  <>
                    <Button
                      onClick={() => { setDetailUser(null); handleImpersonate(detailUser.id); }}
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                    >
                      🎭 Impersonner
                    </Button>
                    <Button
                      onClick={() => { setDetailUser(null); setDeleteDialog({ open: true, user: detailUser, confirmText: '' }); }}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      🗑️
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════ Delete Dialog ═══════════════════════ */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, user: null, confirmText: '' }); }}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">⚠️ Confirmer la suppression</DialogTitle>
            <DialogDescription className="text-slate-400">
              Cette action désactivera définitivement le compte.
            </DialogDescription>
          </DialogHeader>
          {deleteDialog.user && (
            <div className="space-y-4 pt-2">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-white font-medium">{deleteDialog.user.full_name}</p>
                <p className="text-slate-400 text-sm">{deleteDialog.user.email}</p>
                <p className="text-slate-400 text-sm">{getRoleInfo(deleteDialog.user.role).label}</p>
              </div>
              <div>
                <p className="text-slate-300 text-sm mb-2">
                  Saisissez le prénom <strong className="text-orange-400">{deleteDialog.user.full_name?.split(' ')[0]}</strong> pour confirmer :
                </p>
                <Input
                  value={deleteDialog.confirmText}
                  onChange={(e) => setDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }))}
                  placeholder={deleteDialog.user.full_name?.split(' ')[0]}
                  className="bg-slate-800 border-slate-600 text-white"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmDelete(); }}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setDeleteDialog({ open: false, user: null, confirmText: '' })} className="flex-1 bg-slate-700 hover:bg-slate-600" disabled={processing === 'delete'}>
                  Annuler
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={processing === 'delete' || deleteDialog.confirmText.toLowerCase() !== deleteDialog.user.full_name?.split(' ')[0]?.toLowerCase()}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {processing === 'delete' ? 'Suppression…' : 'Supprimer'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════ Bulk Delete Dialog ═══════════════════════ */}
      <Dialog open={bulkDeleteDialog.open} onOpenChange={(open) => { if (!open) setBulkDeleteDialog({ open: false, confirmText: '' }); }}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">⚠️ Suppression en masse</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedIds.size} utilisateur(s) sélectionné(s) seront supprimés.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 max-h-48 overflow-y-auto space-y-1">
              {Array.from(selectedIds).map(id => {
                const u = users.find(x => x.id === id);
                if (!u) return null;
                return (
                  <div key={id} className="flex justify-between items-center py-1 px-1">
                    <span className="text-white text-sm">{u.full_name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getRoleInfo(u.role).color}`}>{getRoleInfo(u.role).label}</span>
                  </div>
                );
              })}
            </div>
            <div>
              <p className="text-slate-300 text-sm mb-2">
                Saisissez <strong className="text-red-400">SUPPRIMER</strong> pour confirmer :
              </p>
              <Input
                value={bulkDeleteDialog.confirmText}
                onChange={(e) => setBulkDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }))}
                placeholder="SUPPRIMER"
                className="bg-slate-800 border-slate-600 text-white"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') confirmBulkDelete(); }}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setBulkDeleteDialog({ open: false, confirmText: '' })} className="flex-1 bg-slate-700 hover:bg-slate-600" disabled={processing === 'bulk'}>
                Annuler
              </Button>
              <Button
                onClick={confirmBulkDelete}
                disabled={processing === 'bulk' || bulkDeleteDialog.confirmText !== 'SUPPRIMER'}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {processing === 'bulk' ? 'Suppression…' : `Supprimer ${selectedIds.size} utilisateur(s)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
// Small helper used inside the detail dialog
function InfoCard({ label, value, valueClass = 'text-white' }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-3">
      <p className="text-slate-500 text-xs mb-1">{label}</p>
      <p className={`text-sm font-medium ${valueClass}`}>{value}</p>
    </div>
  );
}
